import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Badge } from '../components/ui/badge';
import {
  ArrowLeft, FileText, Bell, Inbox, Shield, GraduationCap,
  Calendar, ChevronRight, User, Clock, ExternalLink, Link2
} from 'lucide-react';

import { API_URL } from '../utils/api';

const TYPE_STYLES = {
  'case/iep': {
    label: 'IEP', icon: GraduationCap,
    dot: 'bg-[#3B6A9B]', badge: 'bg-[#3B6A9B]/10 text-[#3B6A9B]',
    border: 'border-[#3B6A9B]/20', bg: 'bg-[#3B6A9B]/3',
  },
  'case/met': {
    label: 'MET', icon: Shield,
    dot: 'bg-[#5A8A5A]', badge: 'bg-[#5A8A5A]/10 text-[#5A8A5A]',
    border: 'border-[#5A8A5A]/20', bg: 'bg-[#5A8A5A]/3',
  },
  'document/referral': {
    label: 'Referral', icon: Inbox,
    dot: 'bg-amber-500', badge: 'bg-amber-50 text-amber-700 border border-amber-200',
    border: 'border-amber-200', bg: 'bg-amber-50/50',
  },
  'document/meeting_notice': {
    label: 'Meeting Notice', icon: Bell,
    dot: 'bg-blue-500', badge: 'bg-blue-50 text-blue-700 border border-blue-200',
    border: 'border-blue-200', bg: 'bg-blue-50/50',
  },
};

const STATUS_STYLES = {
  draft: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-50 text-blue-600',
  review: 'bg-amber-50 text-amber-600',
  finalized: 'bg-green-50 text-green-700',
  archived: 'bg-gray-50 text-gray-400',
};

function formatDate(iso) {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  } catch { return iso.slice(0, 10); }
}

function groupByMonth(items) {
  const groups = {};
  for (const item of items) {
    const d = item.date ? new Date(item.date) : null;
    const key = d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : 'Unknown';
    const label = d ? d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : 'Unknown Date';
    if (!groups[key]) groups[key] = { key, label, items: [] };
    groups[key].items.push(item);
  }
  return Object.values(groups).sort((a, b) => b.key.localeCompare(a.key));
}

export default function StudentTimeline() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  const fetchTimeline = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/students/${studentId}/timeline`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Failed to fetch timeline', err);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => { fetchTimeline(); }, [fetchTimeline]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="animate-pulse text-[#595959]">Loading timeline...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="text-center">
          <p className="text-[#595959] mb-4">Student not found.</p>
          <Link to="/products/iep-met" className="text-[#3B6A9B] hover:underline text-sm">Back to IEP/MET</Link>
        </div>
      </div>
    );
  }

  const { student, items, counts } = data;

  const filtered = filter === 'all' ? items : items.filter(i => {
    if (filter === 'cases') return i.item_type === 'case';
    if (filter === 'documents') return i.item_type === 'document';
    return `${i.item_type}/${i.sub_type}` === filter;
  });

  const groups = groupByMonth(filtered);

  const FILTERS = [
    { value: 'all', label: 'All', count: counts.total },
    { value: 'case/iep', label: 'IEP', count: counts.iep_cases },
    { value: 'case/met', label: 'MET', count: counts.met_cases },
    { value: 'document/referral', label: 'Referrals', count: counts.referrals },
    { value: 'document/meeting_notice', label: 'Notices', count: counts.meeting_notices },
  ];

  return (
    <div className="min-h-screen bg-[#FAFAF8]" data-testid="student-timeline">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-[#E5E5E5]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
          <Link to="/products/iep-met" className="text-[#595959] hover:text-[#1C1C1C] transition-colors" data-testid="timeline-back">
            <ArrowLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <div className="w-9 h-9 rounded-full bg-[#3B6A9B]/10 flex items-center justify-center shrink-0">
              <User className="h-4 w-4 text-[#3B6A9B]" />
            </div>
            <div className="min-w-0">
              <h1 className="font-outfit text-base sm:text-lg font-semibold text-[#1C1C1C] truncate" data-testid="timeline-student-name">
                {student.first_name} {student.last_name}
              </h1>
              <div className="flex items-center gap-3 text-xs text-[#595959]">
                {student.grade && <span>Grade {student.grade}</span>}
                {student.date_of_birth && <span>DOB: {student.date_of_birth}</span>}
                {student.disability_category && <span>{student.disability_category}</span>}
              </div>
            </div>
          </div>
          <div className="text-right shrink-0">
            <p className="text-xs text-[#595959]">Timeline</p>
            <p className="text-sm font-medium text-[#1C1C1C]" data-testid="timeline-item-count">{counts.total} items</p>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'IEP Cases', count: counts.iep_cases, color: 'text-[#3B6A9B]', bg: 'bg-[#3B6A9B]/5', Icon: GraduationCap },
            { label: 'MET Cases', count: counts.met_cases, color: 'text-[#5A8A5A]', bg: 'bg-[#5A8A5A]/5', Icon: Shield },
            { label: 'Referrals', count: counts.referrals, color: 'text-amber-600', bg: 'bg-amber-50', Icon: Inbox },
            { label: 'Notices', count: counts.meeting_notices, color: 'text-blue-600', bg: 'bg-blue-50', Icon: Bell },
          ].map(c => (
            <div key={c.label} className={`${c.bg} rounded-xl px-4 py-3 border border-transparent`} data-testid={`summary-${c.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2 mb-1">
                <c.Icon className={`h-3.5 w-3.5 ${c.color}`} />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-[#595959]">{c.label}</span>
              </div>
              <p className={`font-outfit text-xl font-semibold ${c.color}`}>{c.count}</p>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 mb-6 overflow-x-auto pb-1" data-testid="timeline-filters">
          {FILTERS.map(f => (
            <button key={f.value} onClick={() => setFilter(f.value)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors whitespace-nowrap ${
                filter === f.value ? 'bg-[#1C1C1C] text-white' : 'bg-[#F3F4F1] text-[#595959] hover:bg-[#E5E5E5]'
              }`} data-testid={`filter-${f.value.replace('/', '-')}`}>
              {f.label}
              <span className={`ml-1.5 ${filter === f.value ? 'text-white/60' : 'text-[#595959]/50'}`}>{f.count}</span>
            </button>
          ))}
        </div>

        {/* Timeline */}
        {filtered.length === 0 ? (
          <div className="bg-white border border-[#E5E5E5] rounded-2xl p-12 text-center" data-testid="timeline-empty">
            <Clock className="h-10 w-10 text-[#E5E5E5] mx-auto mb-3" />
            <p className="text-sm text-[#595959]">No items match the current filter.</p>
          </div>
        ) : (
          <div className="space-y-8" data-testid="timeline-groups">
            {groups.map(group => (
              <div key={group.key}>
                {/* Month Header */}
                <div className="flex items-center gap-3 mb-4">
                  <Calendar className="h-4 w-4 text-[#595959]" />
                  <h2 className="text-sm font-semibold text-[#595959] uppercase tracking-wider" data-testid={`month-${group.key}`}>{group.label}</h2>
                  <div className="flex-1 h-px bg-[#E5E5E5]" />
                  <span className="text-xs text-[#595959]/60">{group.items.length}</span>
                </div>

                {/* Items */}
                <div className="relative ml-4 pl-6 border-l-2 border-[#E5E5E5] space-y-3">
                  {group.items.map(item => {
                    const typeKey = `${item.item_type}/${item.sub_type}`;
                    const style = TYPE_STYLES[typeKey] || TYPE_STYLES['case/iep'];
                    const ItemIcon = style.icon;
                    const statusStyle = STATUS_STYLES[item.status] || STATUS_STYLES.draft;

                    return (
                      <Link key={item.id} to={item.link}
                        className={`block relative rounded-xl border ${style.border} ${style.bg} p-4 hover:shadow-md hover:-translate-y-px transition-all group`}
                        data-testid={`timeline-item-${item.id}`}>
                        {/* Dot on the timeline line */}
                        <div className={`absolute -left-[calc(1.5rem+5px)] top-5 w-2.5 h-2.5 rounded-full ${style.dot} ring-2 ring-white`} />

                        <div className="flex items-start justify-between gap-3">
                          <div className="flex items-start gap-3 min-w-0 flex-1">
                            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${style.badge}`}>
                              <ItemIcon className="h-4 w-4" />
                            </div>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap mb-1">
                                <Badge className={`text-[10px] px-1.5 py-0 ${style.badge}`}>{style.label}</Badge>
                                {item.context_type && <Badge className="text-[10px] px-1.5 py-0 bg-purple-50 text-purple-600 capitalize">{item.context_type}</Badge>}
                                <Badge className={`text-[10px] px-1.5 py-0 ${statusStyle}`}>{item.status?.replace('_', ' ')}</Badge>
                              </div>
                              <h3 className="text-sm font-medium text-[#1C1C1C] group-hover:text-[#3B6A9B] transition-colors truncate">{item.title}</h3>
                              <div className="flex items-center gap-3 mt-1 text-xs text-[#595959]">
                                {item.date && <span>{formatDate(item.date)}</span>}
                                {item.due_date && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />Due: {item.due_date}</span>}
                                {item.meeting_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />Meeting: {item.meeting_date}</span>}
                                {item.summary_date && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{item.sub_type === 'referral' ? 'Referral' : 'Meeting'}: {item.summary_date}</span>}
                                {item.linked_case_ids?.length > 0 && (
                                  <span className="flex items-center gap-1 text-[#3B6A9B]">
                                    <Link2 className="h-3 w-3" />Linked to {item.linked_case_ids.length} case{item.linked_case_ids.length > 1 ? 's' : ''}
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                          <ChevronRight className="h-4 w-4 text-[#E5E5E5] group-hover:text-[#3B6A9B] transition-colors shrink-0 mt-1" />
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
